import { createContext, useState, useEffect, useContext } from 'react'
import { AuthContext } from '../../../context/AuthContext'
import { SessionContext } from '../../../context/SessionContext'
import { getQuizQuestions, checkQuizCompleted, submitQuiz } from '../service/quizService'

export const QuizContext = createContext()

const QUIZ_SESSIONS = [658, 659, 660]

export const QuizProvider = ({ children }) => {
  const { currentUser } = useContext(AuthContext)
  const { session } = useContext(SessionContext)

  const [questions, setQuestions] = useState([])
  const [preCompleted, setPreCompleted] = useState(null) // null = loading, true/false = known
  const [postCompleted, setPostCompleted] = useState(null)
  const [loading, setLoading] = useState(true)

  const isQuizSession = QUIZ_SESSIONS.includes(session?.id)

  // Load questions and check completion status
  useEffect(() => {
    if (!currentUser?.id || !session?.id || !isQuizSession) {
      setLoading(false)
      setPreCompleted(true)  // skip quiz for non-quiz sessions
      setPostCompleted(true)
      return
    }

    const initialize = async () => {
      try {
        setLoading(true)

        // Fetch questions
        const q = await getQuizQuestions(session.id)
        setQuestions(q)

        // Check pre-quiz
        const preStatus = await checkQuizCompleted(currentUser.id, session.id, 'pre')
        setPreCompleted(preStatus.completed)

        // Check post-quiz
        const postStatus = await checkQuizCompleted(currentUser.id, session.id, 'post')
        setPostCompleted(postStatus.completed)
      } catch (err) {
        console.error('Error initializing quiz:', err)
        // Default to completed so it doesn't block the workspace
        setPreCompleted(true)
        setPostCompleted(true)
      } finally {
        setLoading(false)
      }
    }

    initialize()
  }, [currentUser?.id, session?.id, isQuizSession])

  const submitPreQuiz = async (responses) => {
    try {
      await submitQuiz(currentUser.id, session.id, 'pre', responses)
      setPreCompleted(true)
    } catch (err) {
      console.error('Error submitting pre-quiz:', err)
      throw err
    }
  }

  const submitPostQuiz = async (responses) => {
    try {
      await submitQuiz(currentUser.id, session.id, 'post', responses)
      setPostCompleted(true)
    } catch (err) {
      console.error('Error submitting post-quiz:', err)
      throw err
    }
  }

  return (
    <QuizContext.Provider value={{
      questions,
      isQuizSession,
      preCompleted,
      postCompleted,
      loading,
      submitPreQuiz,
      submitPostQuiz,
    }}>
      {children}
    </QuizContext.Provider>
  )
}